import React from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';

import type { WelcomeSeriesMountProps } from './WelcomeSeriesMount.tsx';

// No UFOLoadHold: an optional card must not hold page-load metrics.
const LazyWelcomeSeriesMount: LoadableType<WelcomeSeriesMountProps> = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-WelcomeSeriesMount" */ './WelcomeSeriesMount.tsx'))
			.WelcomeSeriesMount,
});

export type WelcomeSeriesMountLoaderProps = Pick<WelcomeSeriesMountProps, 'contentId'>;

/** Renders nothing, and loads no card code, unless one of the two gates is on. */
export const WelcomeSeriesMountLoader = ({
	contentId,
}: WelcomeSeriesMountLoaderProps): React.JSX.Element | null => {
	let variant: WelcomeSeriesMountProps['variant'] | null = null;
	// The internal gate wins so staff keep the eligibility-free bypass.
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('confluence_frontend_loom_welcome_series_internal')) {
		variant = 'internal';
	} else if (fg('confluence_frontend_loom_welcome_series_enabled')) {
		variant = 'production';
	}

	if (!variant) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution={Attribution.CONTENT_TYPES}>
			<LazyWelcomeSeriesMount contentId={contentId} variant={variant} />
		</TransparentErrorBoundary>
	);
};
